import { useEffect } from 'react'
import { orders, projects, quoteProposals, settlementDetails } from '../data/adminExtendedData'
import { settlementBatches } from '../data/mockData'

function text(value) { return String(value || '').replace(/\s+/g, ' ').trim() }

function selectedQuoteNumbers() {
  return new Set(orders.map((order) => order.quoteNo).filter(Boolean))
}

function settlementProject(row) {
  const linkedOrder = orders.find((order) => order.director === row.director && Number(order.amount) === Number(row.gross))
  if (!linkedOrder) return null
  return projects.find((project) => project.orderId === linkedOrder.id || project.orderNo === linkedOrder.orderNo) || null
}

function normalizeSettlementPreviewData() {
  Object.entries(settlementDetails).forEach(([batchId, sourceRows]) => {
    if (!Array.isArray(sourceRows)) return

    // 정산 편입 기준은 결제일이 아니라 프로젝트 최종 확정/완료입니다.
    // 현재 데모 데이터에서 아직 진행 중인 프로젝트와 명확히 연결되는 행은 정산 대상에서 제외합니다.
    const rows = sourceRows.filter((row) => {
      const project = settlementProject(row)
      if (!project) return true
      return project.status === 'COMPLETED' || project.currentStage === '진행 완료'
    })

    settlementDetails[batchId] = rows
    const batch = settlementBatches.find((item) => item.id === batchId)
    if (!batch) return

    // 회차 요약과 상세 행 수/금액이 서로 다르게 보이지 않도록 같은 행 집합으로 다시 계산합니다.
    batch.directorCount = rows.length
    batch.grossAmount = rows.reduce((sum, row) => sum + Number(row.gross || 0), 0)
    batch.pgFeeAmount = rows.reduce((sum, row) => sum + Number(row.pgFee || 0), 0)
    batch.adjustmentAmount = rows.reduce((sum, row) => sum + Number(row.adjustment || 0), 0)
    batch.payoutAmount = rows.reduce((sum, row) => sum + Number(row.payout || 0), 0)
  })
}

// 주문이 생성된 견적은 더 이상 단순 제출 상태일 수 없습니다.
// App 렌더 전에 원본 데모 데이터를 보정해 목록·상세·타임라인이 같은 상태를 사용하게 합니다.
const selectedAtSource = selectedQuoteNumbers()
quoteProposals.forEach((quote) => {
  if (selectedAtSource.has(quote.quoteNo) && quote.status === 'SUBMITTED') quote.status = 'SELECTED'
})

// 관리자와 사용자 화면은 프로젝트 상태를 별도 체계로 만들지 않습니다.
// 기존 READY / IN_PROGRESS / COMPLETED 데모 상태를 공통 5단계의 현재 단계로 정규화합니다.
projects.forEach((project) => {
  if (project.status === 'COMPLETED') {
    project.currentStage = '진행 완료'
    project.progress = 100
  } else if (project.status === 'IN_PROGRESS') {
    project.currentStage = '상담·시안'
  } else if (project.status === 'READY') {
    project.currentStage = '결제 완료'
  }

  const current = project.currentStage
  const completed = project.status === 'COMPLETED' || current === '진행 완료'
  project.milestones = [
    { label: '견적 선택', date: project.startedAt || project.eventDate, status: 'DONE' },
    { label: '결제 완료', date: project.startedAt || project.eventDate, status: current === '결제 완료' ? 'CURRENT' : 'DONE' },
    { label: '상담·시안', date: project.eventDate, status: current === '상담·시안' ? 'CURRENT' : current === '최종 확정' || completed ? 'DONE' : 'UPCOMING' },
    { label: '최종 확정', date: project.eventDate, status: current === '최종 확정' ? 'CURRENT' : completed ? 'DONE' : 'UPCOMING' },
    { label: '진행 완료', date: project.completedAt || project.eventDate, status: completed ? 'DONE' : 'UPCOMING' },
  ]
})

normalizeSettlementPreviewData()

function fixLegacyRenderedBadge() {
  const selected = selectedQuoteNumbers()
  document.querySelectorAll('.admin-content table.data-table tbody tr').forEach((row) => {
    const cells = [...row.querySelectorAll('td')]
    const quoteNo = text(cells[0]?.querySelector('strong')?.textContent || cells[0]?.textContent)
    if (!selected.has(quoteNo)) return
    const badge = row.querySelector('.status-badge')
    if (badge && text(badge.textContent) === '제출') {
      badge.textContent = '견적 선택'
      badge.className = 'status-badge status-info'
      badge.title = '연결된 주문이 생성되어 선택 완료된 견적입니다.'
    }
  })
}

function apply() {
  const route = (window.location.hash.replace(/^#/, '') || '/').split('?')[0]
  if (route === '/quotes' || /^\/quotes\/[^/]+$/.test(route)) fixLegacyRenderedBadge()
}

export default function AdminCommerceParityEnhancer() {
  useEffect(() => {
    let queued = false
    const schedule = () => {
      if (queued) return
      queued = true
      queueMicrotask(() => { queued = false; apply() })
    }
    apply()
    const observer = new MutationObserver(schedule)
    observer.observe(document.body, { childList: true, subtree: true })
    window.addEventListener('hashchange', schedule)
    return () => { observer.disconnect(); window.removeEventListener('hashchange', schedule) }
  }, [])
  return null
}
